import React from 'react';
import { PrintOptions } from './InterviewPrintOptions';
import './PrintableInterview.css';

interface Interview {
  question: string;
  example: string;
  guidance: string;
  answer: string;
}

interface PrintableInterviewProps {
  interview: Interview[];
  jobTitle: string;
  company: string;
  jobDescription: string;
  options: PrintOptions;
}

const PrintableInterview: React.FC<PrintableInterviewProps> = ({
  interview,
  jobTitle,
  company,
  jobDescription,
  options
}) => {
  return (
    <div id="printable-interview" className="print-content" style={{ display: 'none' }}>
      <div className="max-w-4xl mx-auto p-8 bg-white text-black">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Interview Preparation</h1>
          <div className="text-gray-600">Generated by ResumeTitan</div>
        </div>

        {/* Job Information */}
        {options.includeJobInfo && (jobTitle || company || jobDescription) && (
          <div className="mb-8 p-4 border border-gray-300 rounded print-section">
            <h2 className="text-xl font-bold mb-4">Job Information</h2>
            {company && (
              <div className="mb-2">
                <span className="font-semibold">Company: </span>
                <span>{company}</span>
              </div>
            )}
            {jobTitle && (
              <div className="mb-2">
                <span className="font-semibold">Position: </span>
                <span>{jobTitle}</span>
              </div>
            )}
            {jobDescription && (
              <div className="mb-2">
                <span className="font-semibold">Job Description: </span>
                <div className="mt-2 text-sm leading-relaxed whitespace-pre-wrap">{jobDescription}</div>
              </div>
            )}
          </div>
        )}

        {/* Interview Questions */}
        <div className="space-y-8">
          <h2 className="text-2xl font-bold mb-6">Interview Questions</h2>
          
          {interview.map((item, index) => (
            <div key={index} className="print-question border-b border-gray-200 pb-6 mb-6 last:border-b-0">
              {/* Question */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2 print-question-title">
                  Question {index + 1}: {item.question}
                </h3>
              </div>

              {/* My Answer */}
              {options.includeMyAnswers && item.answer && (
                <div className="mb-4">
                  <h4 className="font-semibold text-teal-800 mb-2">My Answer:</h4>
                  <div className="pl-4 border-l-4 border-teal-200 text-sm leading-relaxed whitespace-pre-wrap">
                    {item.answer}
                  </div>
                </div>
              )}

              {/* Example Answer */}
              {options.includeExampleAnswers && item.example && (
                <div className="mb-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Example Answer:</h4>
                  <div className="pl-4 border-l-4 border-blue-200 text-sm leading-relaxed whitespace-pre-wrap">
                    {item.example}
                  </div>
                </div>
              )}

              {/* Guidance */}
              {options.includeGuidance && item.guidance && (
                <div className="mb-4">
                  <h4 className="font-semibold text-green-800 mb-2">Guidance & Tips:</h4>
                  <div className="pl-4 border-l-4 border-green-200 text-sm leading-relaxed whitespace-pre-wrap">
                    {item.guidance}
                  </div>
                </div>
              )}

              {/* Space for notes if no answers are included */}
              {!options.includeMyAnswers && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-600 mb-2">Notes:</h4>
                  <div className="border border-gray-300 rounded p-4 min-h-[100px] bg-gray-50 print-notes-space">
                    {/* Empty space for handwritten notes */}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-gray-300 text-center text-sm text-gray-600">
          <p>Good luck with your interview!</p>
          <p className="mt-1">Generated on {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
};

export default PrintableInterview; 